/**
 * 💌🥊 Rival Callouts — polls the backend for friend-circle events
 * (hearts milestones, future rank climbs) and drops a friendly toast
 * for each unseen event, then marks them seen so the same toast never
 * fires twice on the same device.
 *
 * Owner canon Mar 3, 2026: non-toxic. Celebrates the generous friend,
 * does NOT call out the "loser". Rate-limited to once every 5 minutes
 * per browser tab so it never spams.
 */
import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 5 * 60 * 1000;   // 5 minutes
const SEEN_KEY = "omni:rival-callouts-seen-v1";

function readSeen() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
  catch { return new Set(); }
}
function writeSeen(set) {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set].slice(-200))); }
  catch { /* noop */ }
}

export default function useRivalCallouts(userId) {
  const timer = useRef(null);
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    async function tick() {
      try {
        const { data } = await axios.get(`${API}/rival-callouts`);
        if (cancelled) return;
        const seen = readSeen();
        const fresh = (data.events || []).filter((e) => {
          const key = `${e.event_type}:${e.actor_id}:${e.milestone || e.board || e.created_at}`;
          return !seen.has(key);
        });
        for (const e of fresh) {
          if (e.event_type === "hearts_milestone") {
            toast(`💌🥊 ${e.actor_name}`, {
              description:
                `just crossed ${Number(e.milestone).toLocaleString()} lifetime ` +
                `Friendship Hearts 💕 sent — pass the vibes on.`,
              duration: 8000,
            });
          } else if (e.event_type === "rank_climb") {
            const board = e.board_label || e.board || "the board";
            toast(`🥊 ${e.actor_name}`, {
              description:
                `just passed you on ${board}${e.from && e.to ? ` — ${e.from} → ${e.to}` : ""}. ` +
                `Take back your spot when you're ready.`,
              duration: 8000,
            });
          }
          const key = `${e.event_type}:${e.actor_id}:${e.milestone || e.board || e.created_at}`;
          seen.add(key);
        }
        writeSeen(seen);
        if (fresh.length > 0) {
          try { await axios.post(`${API}/rival-callouts/mark-seen`); } catch { /* noop */ }
        }
      } catch { /* silent — this is a background poll */ }
    }

    tick();
    timer.current = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, [userId]);
}
